import React, { useCallback, useState } from 'react';

import Home, { AccountRouter } from '@bessemercws/accounts';
import { useAuthService } from '@bessemercws/auth';
import { NotFound, NotAuthorized, PendingUserType, UnknownUserType } from '@bessemercws/components';
import DocumentRouter from '@bessemercws/documents';
import { FeatureFlag, FeatureFlags } from '@bessemercws/feature-flags';
import { ApolloError, GetUserProfileDataQuery, isUserProfileType, useGetUserProfileDataQuery, ValidatePassword } from '@bessemercws/graphql';
import InsightRouter from '@bessemercws/insights';
import { LoginLayout, Login } from '@bessemercws/login';
import { App as PreferencesRouter } from '@bessemercws/preferences';
import ProspectHome from '@bessemercws/prospect';
import { useClientContext } from '@bessemercws/providers';
import { normalizePath } from '@bessemercws/utils';
import { Route, useNavigate, useLocation } from 'react-router-dom';

import Quicken from '#/components/Quicken';
import { CustomBody, WithHeader } from '#/layouts/index';
import { mapTermsObservables } from '#/mappers/termsAndConditionsFlag.mapper';
import { useGraphqlService } from '#/services/graphql.service';
import { ProtectedRoute } from './ProtectedRoute';
import { routesByUserProfileType } from './UserProfileTypeRoutesConfig';

function getHomeRoute(userProfileType?: string | undefined | null): string {
  if (userProfileType && isUserProfileType(userProfileType) && routesByUserProfileType[userProfileType]) {
    return routesByUserProfileType[userProfileType].homeRoute
  } else {
    return '/';
  }
}

export const ClientRoutes = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    setIdToken: setContextToken,
    setSelectedClient,
    trackingId,
    setUserId,
    idToken,
    userId,
    setAuthSessionId,
    setAuthSessionToken,
    subscriptionChannel,
    setUserProfileType,
    userProfileType,
    terms_conditions_flag,
    setTermsFlag,
    handleViewerError,
  } = useClientContext();
  const { setToken: setStorageToken, getToken, isTokenValid } = useAuthService();
  const { updateUserObservables, trackLogin } = useGraphqlService();
  const [hasUserIdHash, setHasUserIdHash] = useState(false);

  const isOnLoginPage = normalizePath(location.pathname) === '/login';  
  // Initialize as complete only if on login page, otherwise wait for query
  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(() => isOnLoginPage);
  
  const skipCondition = (!getToken() || !isTokenValid() || (isOnLoginPage && !terms_conditions_flag));
  
  useGetUserProfileDataQuery({
    errorPolicy: 'none',
    skip: skipCondition,
    onCompleted: (data: GetUserProfileDataQuery) => {
      const userProfileTypeResponse = data?.viewer?.user_profile_type;
      setHasUserIdHash(!!data?.viewer?.user_id_hash);
      setUserProfileType(userProfileTypeResponse ?? undefined);
      if (isOnLoginPage && terms_conditions_flag) {
        navigate(getHomeRoute(userProfileTypeResponse));
      }
      setIsInitialLoadComplete(true);
    },
    onError: (error: ApolloError) => {
      if (isOnLoginPage) {
        navigate(getHomeRoute());
      }
      handleViewerError(error?.message ?? 'Unknown error');
      setIsInitialLoadComplete(true);
    },
  });

  const handleNavigateHome = useCallback(() => {
    navigate(getHomeRoute(userProfileType));
  }, [userProfileType, navigate]);

  const handleLoginComplete = async (idToken?: string, validatePassword?: ValidatePassword) => {
    const profile = validatePassword?.user?.profile;
    if (idToken && profile) {
      //set okta auth session token (used for Black Diamond - Consolidated Reporting)
      setAuthSessionToken(validatePassword?.authSessionToken);

      //set okta auth session id so that it can be tracked in DynamoDB. Bill Manager also uses auth session id.
      setAuthSessionId(validatePassword?.authSessionId);

      //track user login - updates last login date in dynamo
      trackLogin();

      await updateUserObservables(mapTermsObservables(profile));
    }
  };

  const handleOnHasAccess = () => {
    setTermsFlag(true);
  };

  const handleAuthorization = (token?: string) => {
    if (token) {
      setStorageToken(token);
      setContextToken(token);
    }
  };

  function handleClientEmulationBegin(clientId: string) {
    setSelectedClient(clientId);
  }

  const handleSetUsername = (username: string) => {
    setUserId(username);
  };

  let userProfileTypeWithHeaderRouteElements: React.JSX.Element[], userProfileTypeCustomBodyRouteElements: React.JSX.Element[];
  if (userProfileType && isUserProfileType(userProfileType) && routesByUserProfileType[userProfileType]) {
    const { withHeaderRoutes, customBodyRoutes } = routesByUserProfileType[userProfileType]
      .routes({ 
        token: idToken,
        clientId: userId ?? undefined,
        subscriptionChannel,
        userProfileType,
        onNavigateHome: handleNavigateHome
      });
    userProfileTypeWithHeaderRouteElements = withHeaderRoutes
      .map(({ path, element }, index) => <Route key={index} path={path} element={element} />);
    userProfileTypeCustomBodyRouteElements = customBodyRoutes
      .map(({ path, element }, index) => <Route key={index} path={path} element={element} />);
  }

  return (
    <>
      <Route index path="notauthorized/*" element={<NotAuthorized onNavigateHome={handleNavigateHome} />} />
      <Route
        path="login/*"
        element={
          <LoginLayout
            trackingId={trackingId}
            onComplete={handleLoginComplete}
            onClientEmulationBegin={handleClientEmulationBegin}
            setUsername={handleSetUsername}
            onAuthorize={handleAuthorization}
            token={idToken}
            onHasAccess={handleOnHasAccess}
          />
        }
      >
        <Route index element={<Login />} />
        <Route index path="*" element={<NotFound onNavigateHome={handleNavigateHome} />} />
      </Route>
      { renderRoutes() }
    </>
  );

  function renderRoutes(): React.JSX.Element {
    if (!isInitialLoadComplete) {
      return (
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <WithHeader />
            </ProtectedRoute>
          }
        ></Route>
      )
    } else if (!userProfileType) {
      return renderRoutesWithoutUserProfileType();
    } else if (userProfileType && userProfileTypeWithHeaderRouteElements && userProfileTypeCustomBodyRouteElements) {
      return renderRoutesWithUserProfileType();
    }
    return renderDefaultRoutes()
  }

  function renderRoutesWithoutUserProfileType() {
    return (
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <WithHeader />
          </ProtectedRoute>
        }
      >
        <Route index element={hasUserIdHash ? <UnknownUserType /> : <PendingUserType />} />
        <Route path="preferences/*" element={<PreferencesRouter />} />
        <Route path="*" element={<NotFound onNavigateHome={handleNavigateHome} />} />
      </Route>
    )
  }

  function renderRoutesWithUserProfileType() {
    return (
      <>
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <WithHeader />
            </ProtectedRoute>
          }
        >
          { userProfileTypeWithHeaderRouteElements }
          <Route path="*" element={<NotFound onNavigateHome={handleNavigateHome} />} />
        </Route>
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <CustomBody />
            </ProtectedRoute>
          }
          >
            { userProfileTypeCustomBodyRouteElements }
            <Route path="*" element={<NotFound onNavigateHome={handleNavigateHome} />} />
          </Route>
      </>
    )
  }

  function renderDefaultRoutes() {
    return (
      <>
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <WithHeader />
            </ProtectedRoute>
          }
        >
          <Route index element={<Home subscriptionChannel={''} />} />
          <Route index path="prospect/*" element={<FeatureFlag
            featureName={FeatureFlags.PROSPECT_HOME_FLAG}
            // TODO: remove once feature is complete: https://bessemertrust.atlassian.net/browse/CWR-4444
            forceTreatment='on'
            featureComponent={ProspectHome}
            featureComponentProps={{}}
          />} />
          <Route path="preferences/*" element={<PreferencesRouter />} />
          <Route path="insights/*" element={<InsightRouter />} />
          <Route path="quicken/*" element={<Quicken />} />
        </Route>
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <CustomBody />
            </ProtectedRoute>
          }
        >
          <Route
            path="account/*"
            element={<AccountRouter authSessionId={idToken} subscriptionChannel={subscriptionChannel} />}
          />
          <Route
            path="documents/*"
            element={
              <DocumentRouter token={idToken} clientId={userId || undefined} subscriptionChannel={subscriptionChannel} onNavigateHome={handleNavigateHome} />
            }
          />
          <Route path="*" element={<NotFound onNavigateHome={handleNavigateHome} />} />
        </Route>
      </>
    )
  }
};

export default ClientRoutes;

// fix overlap in token usage, again
